/**
 * Turning one-click OFF.
 *
 * Deleting the browser's agent key stops THIS browser trading with it, but the
 * on-chain approval stays valid until it expires (up to 30 days), so a copied
 * key could still sign orders (#8). The toggle therefore offers an on-chain
 * revoke (a gas transaction from the main wallet) and reports a failed revoke
 * instead of swallowing it. After a failed revoke the agent is kept, disabled
 * (it can no longer sign here), so the revoke can be retried -- AND recorded in
 * a separate pending-revoke list, so re-enabling one-click cannot strand a live
 * approval by overwriting the only record of its address.
 */
import { ethers } from "ethers";
import { Web3Provider } from "@ethersproject/providers";
import {
  addPendingRevoke,
  dropPendingRevoke,
  getPendingRevokes,
  getStoredAgent,
  putStoredAgent,
  removeStoredAgent,
} from "./oneClick.storage";

const ZERO = "0x0000000000000000000000000000000000000000";
export const REVOKE_AGENT_ABI = ["function revokeAgent(address agent) external"];

export class OneClickRevokeError extends Error {
  readonly expiry?: number;

  constructor(reason: string, expiry?: number) {
    super(
      `One-click is off in this browser, but the on-chain revoke failed: ${reason}. ` +
        "The approval stays valid until it expires unless a revoke succeeds."
    );
    this.name = "OneClickRevokeError";
    this.expiry = expiry;
  }
}

/**
 * The contracts the agent was approved on: the shared registry when the chain
 * has one, otherwise each engine it was approved on separately.
 */
export const revokeTargets = (opts: {
  agentRegistry?: string;
  perpEngine?: string;
  spotEngine?: string;
}): string[] => {
  const real = (a?: string): a is string => !!a && a !== ZERO;
  if (real(opts.agentRegistry)) return [opts.agentRegistry];
  return Array.from(new Set([opts.perpEngine, opts.spotEngine].filter(real)));
};

type RevokeContract = {
  callStatic: { revokeAgent: (agent: string) => Promise<unknown> };
  revokeAgent: (agent: string) => Promise<{ wait: () => Promise<unknown> }>;
};
export type ConnectRevoker = (address: string, provider: Web3Provider) => RevokeContract;

const connectRevoker: ConnectRevoker = (address, provider) =>
  new ethers.Contract(address, REVOKE_AGENT_ABI, provider.getSigner()) as unknown as RevokeContract;

/**
 * Revoke `agent` on every target. Each call is dry-run first, so a contract
 * without `revokeAgent` (or a revert) fails before the wallet asks for gas.
 */
export async function revokeAgentOnChain(
  provider: Web3Provider,
  agent: string,
  targets: string[],
  connect: ConnectRevoker = connectRevoker
): Promise<void> {
  if (!targets.length) throw new Error("no approval contract is configured on this network");
  // Sequential on purpose: one wallet prompt at a time.
  // eslint-disable-next-line no-restricted-syntax
  for (const target of targets) {
    const contract = connect(target, provider);
    // eslint-disable-next-line no-await-in-loop
    await contract.callStatic.revokeAgent(agent);
    // eslint-disable-next-line no-await-in-loop
    const tx = await contract.revokeAgent(agent);
    // eslint-disable-next-line no-await-in-loop
    await tx.wait();
  }
}

const reasonOf = (e: unknown): string => {
  const err = e as { code?: string | number; reason?: string; message?: string };
  if (err?.code === "ACTION_REJECTED" || err?.code === 4001) {
    return "the transaction was rejected in your wallet";
  }
  return err?.reason || err?.message || "unknown error";
};

/**
 * Disable one-click. Without `revokeOnChain` the key is discarded locally only
 * ("Just this browser"). With it, the approval is revoked on-chain first: on
 * success the key is discarded; on failure it is kept DISABLED and
 * OneClickRevokeError is thrown so the caller can say so and offer a retry.
 */
export async function disableOneClick(
  account: string,
  chainId: number,
  opts?: {
    revokeOnChain?: boolean;
    /** The wallet, or a getter for it (resolved inside, so its failure is reported too). */
    provider?: Web3Provider | (() => Promise<Web3Provider>);
    perpEngine?: string;
    spotEngine?: string;
    agentRegistry?: string;
    connect?: ConnectRevoker;
  }
): Promise<void> {
  const stored = getStoredAgent(account, chainId);
  if (!opts?.revokeOnChain || !stored) {
    removeStoredAgent(account, chainId);
    return;
  }
  try {
    if (!opts.provider) throw new Error("no wallet is connected");
    const provider =
      typeof opts.provider === "function" ? await opts.provider() : opts.provider;
    await revokeAgentOnChain(provider, stored.agentAddress, revokeTargets(opts), opts.connect);
  } catch (e) {
    putStoredAgent(account, chainId, { ...stored, enabled: false });
    // Durable record, under its own key. The disabled agent above is enough for
    // an immediate retry, but it is overwritten the moment one-click is
    // re-enabled -- which used to lose the address of a STILL-APPROVED agent and
    // leave no way to revoke it for the rest of its 30 days.
    addPendingRevoke(account, chainId, {
      agentAddress: stored.agentAddress,
      expiry: stored.expiry,
      agentPrivateKey: stored.agentPrivateKey,
    });
    throw new OneClickRevokeError(reasonOf(e), stored.expiry);
  }
  dropPendingRevoke(account, chainId, stored.agentAddress);
  removeStoredAgent(account, chainId);
}

/**
 * Revoke ONE agent that is still approved on-chain after an earlier failure,
 * named by address rather than "whatever is stored" -- by the time the user gets
 * to it, the stored agent may be a different, working one.
 *
 * Dropped from the pending list only on success; a failure leaves it there to
 * try again.
 */
export async function revokePendingAgent(
  account: string,
  chainId: number,
  agentAddress: string,
  opts: {
    provider?: Web3Provider | (() => Promise<Web3Provider>);
    perpEngine?: string;
    spotEngine?: string;
    agentRegistry?: string;
    connect?: ConnectRevoker;
  }
): Promise<void> {
  const pending = getPendingRevokes(account, chainId).find(
    (p) => p.agentAddress.toLowerCase() === agentAddress.toLowerCase()
  );
  if (!pending) return;
  try {
    if (!opts.provider) throw new Error("no wallet is connected");
    const provider =
      typeof opts.provider === "function" ? await opts.provider() : opts.provider;
    await revokeAgentOnChain(provider, pending.agentAddress, revokeTargets(opts), opts.connect);
  } catch (e) {
    throw new OneClickRevokeError(reasonOf(e), pending.expiry);
  }
  dropPendingRevoke(account, chainId, pending.agentAddress);
}
